"use client"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import IconBackArrow from "@/app/(site)/_components/Atoms/Icons/IconBackArrow"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { FOCUS_VISIBLE_BORDER_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { useLangCode } from "@/src/utils/locale/useLangCode"
import { t } from "@/translations"
import { useEffect, useRef, useState } from "react"

type ButtonItem = {
    title?: string
    uri: string
}

type Props = {
    buttons: ButtonItem[]
    parentUri?: string
}

const pillBase = clsx(
    "relative flex h-40 shrink-0 items-center justify-center border border-border-neutral",
    "transition-colors duration-250 ease-out",
    "lg:hover:z-10 lg:hover:border-col-border-strong",
    "focus-visible:z-10 focus-visible:border-col-border-strong",
    FOCUS_VISIBLE_BORDER_CLASS
)

export default function SubcategoryPillsScroller({ buttons, parentUri }: Props) {
    const langCode = useLangCode()
    const scrollRef = useRef<HTMLDivElement>(null)
    const [showLeft, setShowLeft] = useState(false)
    const [showRight, setShowRight] = useState(false)

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return

        const update = () => {
            setShowLeft(el.scrollLeft > 0)
            setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1)
        }

        update()
        el.addEventListener("scroll", update, { passive: true })

        const ro = new ResizeObserver(update)
        ro.observe(el)

        return () => {
            el.removeEventListener("scroll", update)
            ro.disconnect()
        }
    }, [])

    const scroll = (direction: "left" | "right") => {
        const el = scrollRef.current
        if (!el) return
        el.scrollBy({ left: direction === "right" ? 200 : -200, behavior: "smooth" })
    }

    const hasParent = !!parentUri

    return (
        <section className="relative px-12 pb-24 sm:px-16 lg:px-24 lg:pb-32">
            {/* Left fade gradient */}
            <div
                className={clsx(
                    "pointer-events-none absolute top-0 z-10 h-40 w-40 transition-all duration-150 lg:hidden",
                    showLeft ? "left-11 opacity-100 sm:left-16" : "left-0 opacity-0"
                )}
                style={{
                    background:
                        "linear-gradient(90deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)"
                }}
            />

            {/* Prev button */}
            {showLeft && (
                <button
                    type="button"
                    onClick={() => scroll("left")}
                    aria-label={t(langCode, "category_page.scroll_left")}
                    className={clsx(
                        "absolute left-24 top-0 z-20 hidden h-40 w-40 items-center justify-center border border-border-neutral bg-white lg:flex",
                        "focus-visible:z-10 focus-visible:border-col-border-strong",
                        FOCUS_VISIBLE_BORDER_CLASS
                    )}
                >
                    <IconArrowRight className="h-16 w-16 rotate-180" />
                </button>
            )}

            <div ref={scrollRef} className="overflow-x-auto scrollbar-hide">
                <div className="flex">
                    {hasParent && (
                        <DynamicLink
                            href={parentUri!}
                            className={clsx(pillBase, "w-48")}
                            aria-label={t(
                                langCode,
                                "category_page.back_to_parent_category"
                            )}
                        >
                            <IconBackArrow className="h-12 w-12" />
                        </DynamicLink>
                    )}
                    {buttons.map((link, index) => {
                        const isFirst = !hasParent && index === 0
                        return (
                            <DynamicLink
                                key={link.uri ?? `pill-${index}`}
                                href={link.uri}
                                className={clsx(
                                    pillBase,
                                    "px-20 Trim_Mono_POC_Text_14_130_0_400_uppercase",
                                    !isFirst && "-ml-px"
                                )}
                            >
                                {link.title}
                            </DynamicLink>
                        )
                    })}
                </div>
            </div>

            {/* Right fade gradient */}
            <div
                className={clsx(
                    "pointer-events-none absolute top-0 z-10 h-40 w-40 transition-all duration-150 lg:hidden",
                    showRight ? "right-11 opacity-100 sm:right-16" : "right-0 opacity-0"
                )}
                style={{
                    background:
                        "linear-gradient(270deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)"
                }}
            />

            {/* Next button */}
            {showRight && (
                <button
                    type="button"
                    onClick={() => scroll("right")}
                    aria-label={t(langCode, "category_page.scroll_right")}
                    className={clsx(
                        "absolute right-24 top-0 z-20 hidden h-40 w-40 items-center justify-center border border-border-neutral bg-white lg:flex",
                        "focus-visible:z-10 focus-visible:border-col-border-strong",
                        FOCUS_VISIBLE_BORDER_CLASS
                    )}
                >
                    <IconArrowRight className="h-16 w-16" />
                </button>
            )}
        </section>
    )
}
